import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import { toast } from 'sonner'

const KEY = 'email-templates'

export interface EmailTemplate {
  id: string
  slug: string
  name: string
  description: string
  subject: string
  variables: string[]
  is_active: boolean
  updated_by: string
  created_at?: string
  updated_at?: string
}

export interface EmailTemplateDetail extends EmailTemplate {
  body_html: string
  base_layout_html?: string
}

export const useEmailTemplates = () => {
  return useQuery<{ templates: EmailTemplate[]; total: number }>({
    queryKey: [KEY],
    queryFn: async () => {
      const { data } = await api.get('/email-templates')
      return data
    },
  })
}

export const useEmailTemplate = (id: string) => {
  return useQuery<EmailTemplateDetail>({
    queryKey: [KEY, id],
    queryFn: async () => {
      const { data } = await api.get(`/email-templates/${id}`)
      return data
    },
    enabled: !!id,
  })
}

export const useUpdateEmailTemplate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<EmailTemplateDetail> }) => {
      const { data } = await api.patch(`/email-templates/${id}`, updates)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY] })
      toast.success('Plantilla actualizada')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error al actualizar plantilla')
    },
  })
}

export const useCreateEmailTemplate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (template: {
      slug: string
      name: string
      description?: string
      subject: string
      body_html: string
      variables?: string[]
      is_active?: boolean
    }) => {
      const { data } = await api.post('/email-templates', template)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY] })
      toast.success('Plantilla creada exitosamente')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error al crear plantilla')
    },
  })
}

export const usePreviewEmailTemplate = () => {
  return useMutation({
    mutationFn: async ({ id, variables }: { id: string; variables?: Record<string, string> }) => {
      const { data } = await api.post(
        `/email-templates/${id}/preview`,
        variables ? { variables } : {}
      )
      return data as { subject: string; html: string }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error al generar preview')
    },
  })
}

export const useResetEmailTemplate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (slug: string) => {
      const { data } = await api.post(`/email-templates/reset/${slug}`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY] })
      toast.success('Plantilla restaurada a su valor por defecto')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error al restaurar plantilla')
    },
  })
}
